import type { User } from 'firebase/auth';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { useCallback, useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';
import {
  Avatar,
  Box,
  ErrorState,
  Flex,
  IntentionsSpotsCard,
  LoadingState,
  ScrollArea,
  Separator,
  Text,
  type IntentionsSpotsSection,
} from '@/components/common';
import { PageTopBar } from '@/components/layout';
import { ChatBubble, ChatInput, ConversationDropdownMenu } from '@/components/messaging';
import { INTENTIONS, type IntentionId } from '@/constants/intentions';
import { useCurrentUser } from '@/contexts';
import { useConversation } from '@/hooks/useConversation';
import { analyticsClient } from '@/lib';
import { type Message } from '@/services';
import { formatLastActive, type UserProfile } from '@/types';
import { firstPhotoUrl } from '@/utils';

/** Treat as "Active now" if last login/activity is within this window. */
const ACTIVE_NOW_MS = 15 * 60 * 1000;

const CHAT_DRAFT_PREFIX = 'chat-draft:';

function chatDraftKey(conversationId: string): string {
  return `${CHAT_DRAFT_PREFIX}${conversationId}`;
}

function readChatDraft(conversationId: string): string {
  return sessionStorage.getItem(chatDraftKey(conversationId)) ?? '';
}

function writeChatDraft(conversationId: string, text: string): void {
  const key = chatDraftKey(conversationId);
  if (text) {
    sessionStorage.setItem(key, text);
  } else {
    sessionStorage.removeItem(key);
  }
}

function isIntentionId(value: string): value is IntentionId {
  return INTENTIONS.some(option => option.id === value);
}

function partnerPresenceLabel(partner: UserProfile, activeNowLabel: string): string {
  const iso = partner.lastActiveAt ?? partner.lastLoggedInAt;
  if (iso) {
    const diffMs = Date.now() - new Date(iso).getTime();
    if (diffMs >= 0 && diffMs < ACTIVE_NOW_MS) return activeNowLabel;
  }
  return partner.lastActive || formatLastActive(iso) || '';
}

function PartnerHeader({ partner, presence }: { partner: UserProfile; presence: string }) {
  const { t } = useTranslation('chat');
  return (
    <Link
      to={`/profile/${partner.uid}`}
      style={{ textDecoration: 'none' }}
      aria-label={t('dm.openProfile', { name: partner.name })}
    >
      <Flex align="center" gap="2">
        <Avatar name={partner.name} src={firstPhotoUrl(partner.photos)} size="3" radius="full" />
        <Flex direction="column" gap="0">
          <Text size="2" truncate wrap="nowrap">
            {partner.name}
          </Text>
          {presence && (
            <Text size="1" color="gray" truncate wrap="nowrap">
              {presence}
            </Text>
          )}
        </Flex>
      </Flex>
    </Link>
  );
}

function ChatShell({
  centerSlot,
  endSlot,
  onBack,
  children,
}: {
  centerSlot?: ReactNode;
  endSlot?: ReactNode;
  onBack: () => void;
  children: ReactNode;
}) {
  return (
    <Flex direction="column" height="100%" minHeight="0">
      <Flex direction="column" flexShrink="0">
        <PageTopBar backButton onBack={onBack} centerSlot={centerSlot} endSlot={endSlot} />
        <Separator size="4" />
      </Flex>
      {children}
    </Flex>
  );
}

function ConversationContent({
  messages,
  isLoading,
  user,
  partner,
  draft,
  setDraft,
  onSubmit,
  canSend,
  bottomRef,
}: {
  messages: Message[];
  isLoading: boolean;
  user: User | null;
  partner: UserProfile | undefined | null;
  draft: string;
  setDraft: (value: string) => void;
  onSubmit: (e: { preventDefault: () => void }) => void;
  canSend: boolean;
  bottomRef: RefObject<HTMLDivElement | null>;
}) {
  return (
    <Flex direction="column" flexGrow="1" minHeight="0">
      <ScrollArea scrollbars="vertical">
        <Box px="5" py="3">
          {isLoading && messages.length === 0 ? (
            <LoadingState />
          ) : (
            <Flex direction="column" gap="3" width="100%" minWidth="0">
              {messages.map(msg => {
                const mine = msg.senderUid === user?.uid;
                return (
                  <Flex
                    key={msg.id}
                    gap="2"
                    justify={mine ? 'end' : 'start'}
                    width="100%"
                    minWidth="0"
                  >
                    {!mine && (
                      <Avatar
                        name={partner?.name ?? '?'}
                        src={firstPhotoUrl(partner?.photos)}
                        size="2"
                        radius="full"
                      />
                    )}
                    <ChatBubble
                      variant={mine ? 'mine' : 'theirs'}
                      createdAt={msg.createdAt}
                      quote={msg.quotedNote}
                    >
                      {msg.text}
                    </ChatBubble>
                  </Flex>
                );
              })}
              <div ref={bottomRef} />
            </Flex>
          )}
        </Box>
      </ScrollArea>

      <ChatInput draft={draft} setDraft={setDraft} onSubmit={onSubmit} canSend={canSend} />
    </Flex>
  );
}

export function ChatPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation('chat');
  const { t: tCommon } = useTranslation('common');
  const { t: tOnboarding } = useTranslation('onboarding');
  const navigate = useNavigate();
  const { user, currentUser } = useCurrentUser();
  const {
    conversation,
    partner,
    messages,
    sendMessage,
    isBlocked,
    flagConversationUnread,
    sharedIntentions,
    sharedSpots,
    isLoading,
    isError,
    refetch,
  } = useConversation(id, user, currentUser);

  const [draft, setDraftState] = useState(() => (id ? readChatDraft(id) : ''));
  const [draftConversationId, setDraftConversationId] = useState(id);
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  if (id !== draftConversationId) {
    setDraftConversationId(id);
    setDraftState(id ? readChatDraft(id) : '');
  }

  const setDraft = useCallback(
    (value: string) => {
      setDraftState(value);
      if (id) writeChatDraft(id, value);
    },
    [id]
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const goInbox = () => navigate('/inbox');

  if (isError) {
    return (
      <ChatShell onBack={goInbox}>
        <ErrorState
          action={{
            label: tCommon('actions.retry'),
            onClick: () => void refetch(),
          }}
        />
      </ChatShell>
    );
  }

  const onSubmit = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !user || !partner || !id || sending) return;

    setSending(true);
    setDraft('');
    void sendMessage({
      conversationId: id,
      senderUid: user.uid,
      receiverUid: partner.uid,
      text,
      createdAt: new Date().toISOString(),
      participants: [user.uid, partner.uid].sort(),
    })
      .then(messageId => {
        analyticsClient.capture('message_sent', {
          conversation_id: id,
          message_id: messageId,
          sender_id: user.uid,
          session_id: analyticsClient.getCurrentSessionId(),
        });
      })
      .catch(err => {
        console.error('Failed to send message:', err);
        setDraft(text);
      })
      .finally(() => setSending(false));
  };

  if (isBlocked) {
    return (
      <ChatShell onBack={goInbox}>
        <ErrorState
          title={t('dm.blocked')}
          action={{ label: t('dm.backToInbox'), onClick: goInbox }}
        />
      </ChatShell>
    );
  }

  if (isLoading && !conversation) {
    return (
      <ChatShell onBack={goInbox}>
        <Flex align="center" justify="center" flexGrow="1" minHeight="0">
          <LoadingState />
        </Flex>
      </ChatShell>
    );
  }

  if (!conversation) {
    return (
      <ChatShell onBack={goInbox}>
        <ErrorState
          title={t('dm.notFound')}
          action={{ label: t('dm.backToInbox'), onClick: goInbox }}
        />
      </ChatShell>
    );
  }

  const intentionLabels = sharedIntentions
    .filter(isIntentionId)
    .map(intentionId => INTENTIONS.find(option => option.id === intentionId))
    .filter((option): option is (typeof INTENTIONS)[number] => Boolean(option))
    .map(option => tOnboarding(option.labelKey));

  const sharedSections: IntentionsSpotsSection[] = [];
  if (intentionLabels.length > 0) {
    sharedSections.push({
      title: t('dm.bothOpenTo'),
      labels: intentionLabels,
      badgeVariant: 'solid',
    });
  }
  if (sharedSpots.length > 0) {
    sharedSections.push({
      title: t('dm.spotsInCommon'),
      labels: sharedSpots,
      badgeVariant: 'outline',
    });
  }

  const canSend = Boolean(draft.trim() && partner && !sending);
  const presence = partner ? partnerPresenceLabel(partner, t('dm.activeNow')) : '';

  return (
    <ChatShell
      onBack={goInbox}
      centerSlot={partner ? <PartnerHeader partner={partner} presence={presence} /> : undefined}
      endSlot={
        <ConversationDropdownMenu
          conversation={conversation}
          partnerName={partner?.name}
          onMarkUnread={flagConversationUnread}
          onArchived={goInbox}
        />
      }
    >
      <Box px="2">
        <IntentionsSpotsCard sections={sharedSections} />
      </Box>
      <ConversationContent
        messages={messages}
        isLoading={isLoading}
        user={user}
        partner={partner}
        draft={draft}
        setDraft={setDraft}
        onSubmit={onSubmit}
        canSend={canSend}
        bottomRef={bottomRef}
      />
    </ChatShell>
  );
}
